import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import useConfirm from "../../hooks/useConfirm";
import {ApiKeyRepository} from "../../repositories/ApiKeyRepository";
import {ApiKeyDialog} from "./components/api-key-dialog";
import {ApiKeyCreatedDialog} from "./components/api-key-created-dialog";

const getErrorMessage = (e, fallback) => e?.response?.data?.message || fallback;

// State and actions for a list of API keys. `fetchKeys` returns the keys to show and is re-run when `deps` change.
// When `organizationId` is given, new keys are always created for that organization.
export const useApiKeys = ({fetchKeys, deps = [], organizations, organizationId}) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [createdKey, setCreatedKey] = useState(null);
  // full keys fetched via reveal, by key id
  const [revealedKeys, setRevealedKeys] = useState({});

  const confirmDelete = useConfirm("Applications using this key will immediately lose access. This cannot be undone.");

  useEffect(() => {
    loadApiKeys();
  }, deps);

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      setApiKeys(await fetchKeys() || []);
      setRevealedKeys({});
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to load API keys"));
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (apiKey = null) => {
    setSelectedKey(apiKey);
    setOpenModal(true);
  };

  const closeDialog = () => {
    setOpenModal(false);
    setSelectedKey(null);
  };

  const handleSave = async ({validUntil, organizationId}) => {
    try {
      if (selectedKey?.id) {
        const response = await ApiKeyRepository.edit(selectedKey.id, validUntil);
        setApiKeys(prev => prev.map(k => k.id === selectedKey.id ? response.data : k));
        toast.success("API key updated");
      } else {
        const response = await ApiKeyRepository.create({validUntil, organizationId});
        const {key, ...masked} = response.data;
        setApiKeys(prev => [masked, ...prev]);
        setCreatedKey(response.data);
      }
      return true;
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to save API key"));
      return false;
    }
  };

  const handleToggleReveal = async (apiKey) => {
    if (revealedKeys[apiKey.id]) {
      setRevealedKeys(({[apiKey.id]: _, ...rest}) => rest);
      return;
    }
    try {
      const response = await ApiKeyRepository.reveal(apiKey.id);
      setRevealedKeys(prev => ({...prev, [apiKey.id]: response.data.key}));
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to reveal API key"));
    }
  };

  const handleCopy = async (apiKey) => {
    try {
      const key = revealedKeys[apiKey.id] || (await ApiKeyRepository.reveal(apiKey.id)).data.key;
      await navigator.clipboard.writeText(key);
      toast.success("API key copied to clipboard");
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to copy API key"));
    }
  };

  const handleDelete = async (apiKey) => {
    const confirmed = await confirmDelete({title: `Revoke key "${apiKey.maskedKey?.slice(0, 12)}..."?`});
    if (!confirmed) return;
    try {
      await ApiKeyRepository.delete(apiKey.id);
      setApiKeys(prev => prev.filter(k => k.id !== apiKey.id));
      toast.success("API key revoked");
    } catch (e) {
      console.error(e);
      toast.error(getErrorMessage(e, "Failed to revoke API key"));
    }
  };

  const dialogs = (
    <>
      <ApiKeyDialog
        open={openModal}
        selectedKey={selectedKey}
        handleClose={closeDialog}
        organizations={organizations}
        organizationId={organizationId}
        onSubmit={handleSave}
      />
      <ApiKeyCreatedDialog apiKey={createdKey} handleClose={() => setCreatedKey(null)}/>
    </>
  );

  return {
    apiKeys,
    loading,
    openCreate: () => openDialog(),
    dialogs,
    tableProps: {
      apiKeys,
      loading,
      revealedKeys,
      onToggleReveal: handleToggleReveal,
      onCopy: handleCopy,
      onEdit: openDialog,
      onDelete: handleDelete,
    },
  };
};
